import moment from 'moment';

import { timePeriod } from '@/constants/timePeriod';
import { DateHelper } from '@/helpers/date.helper';
import { EmployeeStatusesHelper } from '@/helpers/employee-statuses.helper';
/**
 * @typedef {import('@/models/employee-status.interface').EmployeeStatus} EmployeeStatus
 */
/**
 * @template S, R
 * @typedef {import('vuex').ActionTree<S, R>} ActionTree<S, R>
 */
/**
 * @template S
 * @typedef {import('vuex').MutationTree<S>} MutationTree<S>
 */
/**
 * @template S, R
 * @typedef {import('vuex').GetterTree<S, R>} GetterTree<S, R>
 */
/**
 * @template S, R
 * @typedef {import('vuex').Module<S, R>} Module<S, R>
 */
/**
 * @typedef {import('@/store/root.state').RootState} RootState
 */

/**
 * @typedef {Object} DateRange
 * @property {string} startDate
 * @property {string} endDate
 */

/**
 * @typedef {Object} MatrixByCostCenterReportFilter
 * @property {DateRange} dateRangeFilter
 * @property {string} employeeFilter
 * @property {EmployeeStatus | null} employeeStatuses
 * @property {string[]} officeIds
 * @property {string} costCentersFilter
 */

/**
 * @typedef {Object} MatrixByCostCenterReportFilterState
 * @property {MatrixByCostCenterReportFilter} filters
 * @property {boolean} isBillableOnlyShown
 * @property {boolean} isEmptyHidden
 */

/** @returns {MatrixByCostCenterReportFilterState} */
const getInitialState = () => ({
  filters: {
    dateRangeFilter: {
      startDate: DateHelper.toIso(moment().startOf(timePeriod.month)),
      endDate: DateHelper.toIso(moment().endOf(timePeriod.month)),
    },
    employeeFilter: '',
    employeeStatuses: null,
    officeIds: [],
    costCentersFilter: '',
  },
  isBillableOnlyShown: false,
  isEmptyHidden: false,
});

/** @type {MatrixByCostCenterReportFilterState} */
const state = getInitialState();

/** @type {GetterTree<MatrixByCostCenterReportFilterState, RootState>} */
const getters = {
  employeeFilter: (state) => state.filters.employeeFilter,
  dateRangeFilter: (state) => state.filters.dateRangeFilter,
  employeeStatusFilter: (state, getters) => {
    const { employeeStatuses } = state.filters;
    const { employeeStatusFilterOptions } = getters;

    if (employeeStatuses || !employeeStatusFilterOptions.length) return employeeStatuses || [];

    const initialStatuses = EmployeeStatusesHelper.filterDefaultStatuses(employeeStatusFilterOptions);

    return initialStatuses.map((status) => status.title);
  },
  /* eslint-disable no-param-reassign */
  employeeStatusFilterMap: (state, getters) => getters.employeeStatusFilter.reduce((filterMap, employeeStatus) => {
    filterMap[employeeStatus] = employeeStatus;

    return filterMap;
  }, {}),
  /* eslint-enable */
  employeeStatusFilterOptions: (state, getters, rootState, rootGetters) => {
    const matrixReport = rootGetters['matrixByCostCenterReport/report/matrixReport'];

    if (!matrixReport.employees || !matrixReport.employees.length) return [];

    return EmployeeStatusesHelper.getStatusesFromEmployees(matrixReport.employees);
  },
  employeeOfficeFilter: (state) => state.filters.officeIds,
  costCentersFilter: (state) => state.filters.costCentersFilter,
  isBillableOnlyShown: (state) => state.isBillableOnlyShown,
  isEmptyHidden: (state) => state.isEmptyHidden,
};

/** @type {ActionTree<MatrixByCostCenterReportFilterState, RootState>} */
const actions = {
  setEmployeeFilter: ({ commit }, name) => commit('setEmployeeFilter', name),
  setEmployeeStatusFilter: ({ commit }, statuses) => commit('setEmployeeStatusFilter', statuses),
  setEmployeeOfficeFilter: ({ commit }, officeIds) => commit('setEmployeeOfficeFilter', officeIds),
  setCostCentersFilter: ({ commit }, title) => commit('setCostCentersFilter', title),
  changeCustomDateRange({ commit }, dateRange) {
    const newDateRange = {
      startDate: dateRange.start,
      endDate: dateRange.end,
    };

    commit('setDateRangeFilter', newDateRange);
  },
  moveDateRange({ commit }, { isForward, dateRange }) {
    /** @type {moment.DurationInputArg2} */
    let period = timePeriod.month;
    let periodAmount = 1;

    const newDateRange = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };

    if (newDateRange.startDate === DateHelper.toIso(moment(dateRange.startDate).startOf(period))
      && newDateRange.endDate === DateHelper.toIso(moment(dateRange.endDate).endOf(period))) {
      periodAmount = moment(dateRange.endDate).diff(moment(dateRange.startDate), period);
    } else {
      period = timePeriod.day;
      periodAmount = moment(dateRange.endDate).diff(moment(dateRange.startDate), period);
    }

    periodAmount += 1;

    if (isForward) {
      newDateRange.startDate = moment(dateRange.startDate).add(periodAmount, period);
      newDateRange.endDate = period === timePeriod.month
        ? moment(newDateRange.startDate).add(periodAmount - 1, period).endOf(period)
        : moment(dateRange.endDate).add(periodAmount, period);
    } else {
      newDateRange.startDate = moment(dateRange.startDate).subtract(periodAmount, period);
      newDateRange.endDate = period === timePeriod.month
        ? moment(dateRange.endDate).subtract(periodAmount, period).endOf(period)
        : moment(dateRange.endDate).subtract(periodAmount, period);
    }

    newDateRange.startDate = DateHelper.toIso(newDateRange.startDate);
    newDateRange.endDate = DateHelper.toIso(newDateRange.endDate);

    commit('setDateRangeFilter', newDateRange);
  },
  setIsBillableOnlyShown({ commit }, isBillableOnlyShown) {
    commit('setIsBillableOnlyShown', isBillableOnlyShown);
  },
  setIsEmptyHidden({ commit }, isEmptyHidden) {
    commit('setIsEmptyHidden', isEmptyHidden);
  },
};

/** @type {MutationTree<MatrixByCostCenterReportFilterState>} */
const mutations = {
  setEmployeeFilter: (state, data) => (state.filters.employeeFilter = data),
  setEmployeeStatusFilter: (state, statuses) => (state.filters.employeeStatuses = statuses),
  setEmployeeOfficeFilter: (state, officeIds) => (state.filters.officeIds = officeIds),
  setCostCentersFilter: (state, data) => (state.filters.costCentersFilter = data),
  setDateRangeFilter: (state, data) => (state.filters.dateRangeFilter = data),
  setIsBillableOnlyShown: (state, data) => (state.isBillableOnlyShown = data),
  setIsEmptyHidden: (state, data) => (state.isEmptyHidden = data),
  resetModuleState: (state) => Object.assign(state, getInitialState()),
};

/** @type {Module<MatrixByCostCenterReportFilterState, RootState>} */
export const filters = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
